import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import {
  FiPlus,
  FiEdit3,
  FiEye,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiDownload,
  FiUpload,
  FiMoreVertical,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiStar,
  FiMapPin,
  FiUsers
} from 'react-icons/fi';
import { apiClient } from '../../../services/api';
import toast from 'react-hot-toast';
import { ServiceType } from '../../../hooks/useServiceTypes';
import { API_URL } from '../../../services/api';
import ConfirmModal, { ConfirmModalType } from '../../common/ConfirmModal';

interface Company {
  id: string;
  name: string;
  nameEn?: string;
  email: string;
  phone: string;
  province: string;
  district: string;
  services: string[];
  status: 'active' | 'pending' | 'suspended' | 'rejected';
  isVerified: boolean;
  isFeatured: boolean;
  createdAt: string;
  lastUpdated: string;
  viewCount: number;
  contactCount: number;
  logo?: string;
}

interface CompanyListProps {
  companies?: Company[];
  loading?: boolean;
  onEdit?: (company: Company) => void;
  onDelete?: (companyId: string) => void;
  onView?: (company: Company) => void;
  onStatusChange?: (companyId: string, status: Company['status']) => void;
  serviceTypes?: ServiceType[];
  serviceTypesLoading?: boolean;
  serviceTypeFilter?: string;
  onServiceTypeFilterChange?: (value: string) => void;
  verifiedFilter?: string;
  onVerifiedFilterChange?: (value: string) => void;
}

const CompanyList: React.FC<CompanyListProps> = ({
  companies = [],
  loading = false,
  onEdit,
  onDelete,
  onView,
  onStatusChange,
  serviceTypes = [],
  serviceTypesLoading = false,
  serviceTypeFilter = '',
  onServiceTypeFilterChange,
  verifiedFilter = '',
  onVerifiedFilterChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [provinceFilter, setProvinceFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('lastUpdated');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  // Confirm Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: ConfirmModalType;
    onConfirm: () => void;
    confirmText: string;
    isLoading: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {},
    confirmText: 'ยืนยัน',
    isLoading: false
  });

  // Filter and sort companies
  const filteredAndSortedCompanies = useMemo(() => {
    let filtered = companies.filter(company => {
      const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          company.nameEn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          company.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
      const matchesProvince = provinceFilter === 'all' || company.province === provinceFilter;

      // Service Type Filter
      const matchesServiceType = !serviceTypeFilter ||
        company.services.some(service => service === serviceTypeFilter);

      // Verified Filter
      const matchesVerified = !verifiedFilter ||
        (verifiedFilter === 'true' && company.isVerified) ||
        (verifiedFilter === 'false' && !company.isVerified);

      return matchesSearch && matchesStatus && matchesProvince && matchesServiceType && matchesVerified;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = a[sortBy as keyof Company];
      let bValue: any = b[sortBy as keyof Company];

      if (sortBy === 'viewCount' || sortBy === 'contactCount') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [companies, searchTerm, statusFilter, provinceFilter, sortBy, sortOrder, serviceTypeFilter, verifiedFilter]);

  // Pagination
  const totalItems = filteredAndSortedCompanies.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCompanies = filteredAndSortedCompanies.slice(startIndex, endIndex);

  // Get unique provinces for filter
  const provinces = ['all', ...new Set(companies.map(c => c.province))];

  const handleSelectAll = () => {
    if (selectedCompanies.length === currentCompanies.length) {
      setSelectedCompanies([]);
    } else {
      setSelectedCompanies(currentCompanies.map(c => c.id));
    }
  };

  const handleSelectCompany = (companyId: string) => {
    if (selectedCompanies.includes(companyId)) {
      setSelectedCompanies(selectedCompanies.filter(id => id !== companyId));
    } else {
      setSelectedCompanies([...selectedCompanies, companyId]);
    }
  };

  const getStatusBadge = (status: Company['status']) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'ใช้งาน', icon: FiCheckCircle },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'รออนุมัติ', icon: FiClock },
      suspended: { color: 'bg-red-100 text-red-800', label: 'ระงับ', icon: FiXCircle },
      rejected: { color: 'bg-gray-100 text-gray-800', label: 'ปฏิเสธ', icon: FiXCircle }
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const handleBulkAction = (action: string) => {
    console.log(`Bulk action: ${action} for companies:`, selectedCompanies);

    if (selectedCompanies.length === 0) {
      toast.error('กรุณาเลือกบริษัทที่ต้องการดำเนินการ');
      return;
    }

    // Configure modal based on action
    const actionText = action === 'delete' ? 'ลบ' : action === 'activate' ? 'เปิดใช้งาน' : 'ระงับ';
    const modalType: ConfirmModalType = action === 'delete' ? 'danger' : action === 'activate' ? 'success' : 'warning';

    setConfirmModal({
      isOpen: true,
      title: `${actionText}บริษัท`,
      message: `คุณแน่ใจหรือไม่ที่จะ${actionText} ${selectedCompanies.length} บริษัทที่เลือก?`,
      type: modalType,
      confirmText: actionText,
      isLoading: false,
      onConfirm: () => executeBulkAction(action)
    });
  };

  const executeBulkAction = async (action: string) => {
    const actionText = action === 'delete' ? 'ลบ' : action === 'activate' ? 'เปิดใช้งาน' : 'ระงับ';

    // Show loading state
    setConfirmModal(prev => ({ ...prev, isLoading: true }));

    try {
      switch (action) {
        case 'delete':
          await Promise.all(
            selectedCompanies.map(companyId =>
              apiClient.delete(`/api/admin/companies/${companyId}`)
            )
          );
          toast.success(`ลบบริษัท ${selectedCompanies.length} รายการสำเร็จ`);
          break;

        case 'activate':
          await Promise.all(
            selectedCompanies.map(companyId =>
              apiClient.patch(`/api/admin/companies/${companyId}`, { isActive: true })
            )
          );
          toast.success(`เปิดใช้งานบริษัท ${selectedCompanies.length} รายการสำเร็จ`);
          break;

        case 'suspend':
          await Promise.all(
            selectedCompanies.map(companyId =>
              apiClient.patch(`/api/admin/companies/${companyId}`, { isActive: false })
            )
          );
          toast.success(`ระงับการใช้งานบริษัท ${selectedCompanies.length} รายการสำเร็จ`);
          break;
      }

      // Close modal
      setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));

      // Clear selection
      setSelectedCompanies([]);

      // Delay reload to allow toast to show
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error: any) {
      console.error(`Bulk ${action} failed:`, error);
      toast.error(error.response?.data?.message || `เกิดข้อผิดพลาดในการ${actionText}บริษัท`);

      // Close modal on error
      setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
    }
  };

  // Toggle verified status
  const handleToggleVerified = async (company: Company) => {
    const newVerifiedStatus = !company.isVerified;
    try {
      await apiClient.patch(`/api/admin/companies/${company.id}`, {
        isVerified: newVerifiedStatus
      });

      toast.success(
        newVerifiedStatus ? 'ตรวจสอบบริษัทเรียบร้อยแล้ว' : 'ยกเลิกการตรวจสอบเรียบร้อยแล้ว'
      );

      // Refresh data after delay to allow toast to show
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      console.error('Error toggling verified status:', error);
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการอัพเดทสถานะการตรวจสอบ');
    }
  };

  // Toggle featured status
  const handleToggleFeatured = async (company: Company) => {
    const newFeaturedStatus = !company.isFeatured;
    try {
      await apiClient.patch(`/api/admin/companies/${company.id}`, {
        isFeatured: newFeaturedStatus
      });

      toast.success(
        newFeaturedStatus ? 'เพิ่มบริษัทในรายการแนะนำเรียบร้อยแล้ว' : 'ยกเลิกการแนะนำบริษัทเรียบร้อยแล้ว'
      );

      // Refresh data after delay to allow toast to show
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      console.error('Error toggling featured status:', error);
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการอัพเดทสถานะการแนะนำ');
    }
  };

  // Handle Export
  const handleExport = async () => {
    try {
      setIsExporting(true);

      // Get JWT token
      const token = localStorage.getItem('access_token');
      if (!token) {
        toast.error('กรุณาเข้าสู่ระบบก่อน');
        return;
      }

      

      // Call export API
      const response = await fetch(
        `${API_URL}/api/admin/companies/export/excel`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'ไม่สามารถ export ข้อมูลได้');
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `companies-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Export Excel สำเร็จ');
    } catch (error: any) {
      console.error('Export failed:', error);
      toast.error(error.message || 'เกิดข้อผิดพลาดในการ export ข้อมูล');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle Import - Navigate to import page
  const handleImport = () => {
    window.location.href = '/admin/companies/import';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">กำลังโหลดข้อมูลบริษัท...</span>
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-8 text-center">
        <p className="text-gray-500">ไม่พบข้อมูลบริษัท</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">จัดการบริษัท</h2>
          <p className="text-sm text-gray-600 mt-1">
            รายการบริษัทรักษาความปลอดภัยในระบบ ({totalItems} บริษัท)
          </p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          <button
            onClick={handleImport}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <FiUpload className="w-4 h-4 mr-2" />
            Import
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiDownload className="w-4 h-4 mr-2" />
            {isExporting ? 'กำลัง Export...' : 'Export'}
          </button>
          <Link
            to="/admin/companies/add"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <FiPlus className="w-4 h-4 mr-2" />
            เพิ่มบริษัท
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="ค้นหาบริษัท..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="all">ทุกสถานะ</option>
            <option value="active">ใช้งาน</option>
            <option value="pending">รออนุมัติ</option>
            <option value="suspended">ระงับ</option>
            <option value="rejected">ปฏิเสธ</option>
          </select>

          {/* Province Filter */}
          <select
            value={provinceFilter}
            onChange={(e) => setProvinceFilter(e.target.value)}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="all">ทุกจังหวัด</option>
            {provinces.slice(1).map(province => (
              <option key={province} value={province}>{province}</option>
            ))}
          </select>

          {/* Service Type Filter - NEW */}
          <select
            value={serviceTypeFilter}
            onChange={(e) => onServiceTypeFilterChange?.(e.target.value)}
            disabled={serviceTypesLoading}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm disabled:opacity-50"
            title="กรองตามประเภทบริการ"
          >
            <option value="">ทุกประเภทบริการ</option>
            {serviceTypes.map(st => (
              <option key={st.id} value={st.nameTh}>{st.nameTh}</option>
            ))}
          </select>

          {/* Verified Filter - NEW */}
          <select
            value={verifiedFilter}
            onChange={(e) => onVerifiedFilterChange?.(e.target.value)}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
            title="กรองตามสถานะการตรวจสอบ"
          >
            <option value="">ทั้งหมด</option>
            <option value="true">✓ ตรวจสอบแล้ว</option>
            <option value="false">ยังไม่ตรวจสอบ</option>
          </select>

          {/* Sort */}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order as 'asc' | 'desc');
            }}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="lastUpdated-desc">อัพเดทล่าสุด</option>
            <option value="name-asc">ชื่อ A-Z</option>
            <option value="name-desc">ชื่อ Z-A</option>
            <option value="viewCount-desc">ยอดดูมากสุด</option>
            <option value="contactCount-desc">ติดต่อมากสุด</option>
            <option value="createdAt-desc">วันที่สร้างล่าสุด</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedCompanies.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-700">
              เลือก {selectedCompanies.length} บริษัท
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => handleBulkAction('activate')}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
              >
                เปิดใช้งาน
              </button>
              <button
                onClick={() => handleBulkAction('suspend')}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
              >
                ระงับ
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
              >
                ลบ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectedCompanies.length === currentCompanies.length && currentCompanies.length > 0}
              onChange={handleSelectAll}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
            />
            <span className="text-sm font-medium text-gray-700">
              เลือกทั้งหมด ({currentCompanies.length})
            </span>
          </div>
        </div>

        <ul className="divide-y divide-gray-200">
          {currentCompanies.map((company) => (
            <li key={company.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center min-w-0 flex-1">
                  <input
                    type="checkbox"
                    checked={selectedCompanies.includes(company.id)}
                    onChange={() => handleSelectCompany(company.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-4"
                  />

                  {/* Company Logo */}
                  <div className="flex-shrink-0 h-12 w-12">
                    {company.logo ? (
                      <img className="h-12 w-12 rounded-lg object-cover" src={company.logo} alt={company.name} />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-gray-300 flex items-center justify-center">
                        <FiUsers className="h-6 w-6 text-gray-600" />
                      </div>
                    )}
                  </div>

                  {/* Company Info */}
                  <div className="min-w-0 flex-1 ml-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {company.name}
                        </p>
                        {company.isVerified && (
                          <FiCheckCircle className="ml-2 h-4 w-4 text-blue-500" />
                        )}
                        {company.isFeatured && (
                          <FiStar className="ml-1 h-4 w-4 text-yellow-500" />
                        )}
                      </div>
                      {getStatusBadge(company.status)}
                    </div>

                    <div className="mt-1">
                      <p className="text-sm text-gray-500">
                        {company.nameEn}
                      </p>
                    </div>

                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <FiMapPin className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                      <span>{company.district}, {company.province}</span>
                      <span className="mx-2">•</span>
                      <span>{company.phone}</span>
                      <span className="mx-2">•</span>
                      <span>{company.viewCount.toLocaleString()} views</span>
                    </div>

                    <div className="mt-1 flex flex-wrap gap-1">
                      {company.services.slice(0, 3).map((service, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {service}
                        </span>
                      ))}
                      {company.services.length > 3 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          +{company.services.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onView?.(company)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                    title="ดูรายละเอียด"
                  >
                    <FiEye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onEdit?.(company)}
                    className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50"
                    title="แก้ไข"
                  >
                    <FiEdit3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete?.(company.id)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50"
                    title="ลบ"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>

                  {/* Toggle Actions Dropdown Menu */}
                  <Menu as="div" className="relative">
                    <Menu.Button className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
                      <FiMoreVertical className="h-4 w-4" />
                    </Menu.Button>
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => handleToggleVerified(company)}
                              className={`${
                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                              } flex w-full items-center px-4 py-2 text-sm`}
                            >
                              {company.isVerified ? (
                                <>
                                  <FiXCircle className="mr-3 h-4 w-4 text-red-500" />
                                  ยกเลิกการตรวจสอบ
                                </>
                              ) : (
                                <>
                                  <FiCheckCircle className="mr-3 h-4 w-4 text-green-500" />
                                  ตรวจสอบแล้ว
                                </>
                              )}
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => handleToggleFeatured(company)}
                              className={`${
                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                              } flex w-full items-center px-4 py-2 text-sm`}
                            >
                              {company.isFeatured ? (
                                <>
                                  <FiStar className="mr-3 h-4 w-4 text-gray-400" />
                                  ยกเลิกแนะนำ
                                </>
                              ) : (
                                <>
                                  <FiStar className="mr-3 h-4 w-4 text-yellow-500" />
                                  แนะนำบริษัทนี้
                                </>
                              )}
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Menu>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              ก่อนหน้า
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              ถัดไป
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                แสดง <span className="font-medium">{startIndex + 1}</span> ถึง{' '}
                <span className="font-medium">{Math.min(endIndex, totalItems)}</span> จาก{' '}
                <span className="font-medium">{totalItems}</span> รายการ
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        isLoading={confirmModal.isLoading}
      />
    </div>
  );
};

export default CompanyList;